import { Controller } from "@hotwired/stimulus"

// The "Talk to sales" modal.
//
// Mounted on the body so every CTA is a descendant.
//
// Rather than tagging each of the fifteen or so buttons, it intercepts clicks
// on links already pointing at the get-started section. That href is the
// existing marker for "this asks for a demo", it stays the no-JavaScript
// fallback, and a CTA added later is picked up without being wired up.
const TRIGGER = `a[href$="#${"get-started"}"]`

export default class extends Controller {
  static targets = ["dialog"]

  connect() {
    this.onClick = (event) => {
      if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey) return
      if (event.target.closest(TRIGGER)) this.open(event)
    }
    this.element.addEventListener("click", this.onClick)

    // This app runs turbo-refresh-method="morph". A showModal dialog left open
    // across a morph blocks the page, and same-URL redirects morph rather than
    // navigate, so close on the way out rather than counting on a full load.
    this.closeBeforeRender = () => this.close()
    document.addEventListener("turbo:before-render", this.closeBeforeRender)
  }

  disconnect() {
    this.element.removeEventListener("click", this.onClick)
    document.removeEventListener("turbo:before-render", this.closeBeforeRender)
  }

  open(event) {
    if (!this.hasDialogTarget) return // not configured; let the href do its job
    if (event) event.preventDefault()

    // Show before loading. A dialog is display:none until it opens, so a frame
    // loaded first measures itself inside a box with no height and reports it,
    // and the panel shrinks to its floor and stays there.
    if (!this.dialogTarget.open) this.dialogTarget.showModal()

    this.embed()
  }

  // The same snippet a customer pastes, written on open rather than rendered
  // with the page: an open is what pays for the conversation.
  embed() {
    if (this.embedded) return
    this.embedded = true

    const { scriptSrc, botUrl, frameTarget } = this.dialogTarget.dataset
    const script = document.createElement("script")
    script.async = true
    script.src = scriptSrc
    script.dataset.botUrl = botUrl
    script.dataset.mode = "inline"
    script.dataset.target = frameTarget
    // Asks for a card rather than taking whatever the flow was saved as. The
    // dialog is the same form the hero panel holds, in a box the same shape.
    script.dataset.variant = "card"
    // This site switches theme with a class rather than following the OS.
    script.dataset.theme = "tailwind_class"
    // Inline waits to be asked, but opening this dialog was the asking: the
    // reader pressed "Talk to sales". Meeting a second button to start would
    // be asking them twice.
    script.dataset.autostart = "true"
    script.dataset.dialogEmbed = "true"
    document.body.appendChild(script)
  }

  // A closed dialog is still in the document, so a widget left inside it keeps
  // whatever step it was on. The same flow runs in the hero panel, so that step
  // is stale as soon as the reader answers anything there. Tear it down and let
  // the next open build from the conversation as it actually stands.
  close() {
    if (!this.hasDialogTarget) return
    if (this.dialogTarget.open) this.dialogTarget.close()
    if (!this.embedded) return

    // A reader can still be running a chatbot.js cached from before it knew how
    // to tear itself down, so falling back to removing the element keeps the
    // close button working for them.
    const widget = this.dialogTarget.querySelector(".ucb-chatbot-widget")
    if (widget?.ucbDestroy) widget.ucbDestroy()
    else widget?.remove()

    document.querySelector("script[data-dialog-embed]")?.remove()
    this.embedded = false
  }

  // A native dialog's backdrop belongs to the dialog element, so a click that
  // lands on the element itself rather than its content came from the backdrop.
  clickOutside(event) {
    if (event.target === this.dialogTarget) this.close()
  }
}
